<template>
  <div class="alert alert-success border-success">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div class="mb-3">
      <div class="h4 fw-bold">Manage Questions</div>
      <div class="fst-italic">
        Add, edit, or delete questions for your survey.
      </div>
    </div>

    <div class="row mb-2 align-items-center">
      <div class="col-1 text-end fw-bold">#</div>
      <div class="col-4 fw-bold">Topic</div>
      <div class="col-4 fw-bold">Question Text</div>
      <div class="col-1 fw-bold text-end">Edit</div>
      <div class="col-1 fw-bold text-end">Links</div>
      <div class="col-1 fw-bold text-end">Delete</div>
    </div>

    <div
      v-for="(question, index) in questions"
      :key="question.uuid"
      class="row mb-2 align-items-center"
    >
      <div class="col-12 col-md-1 text-end">
        <span class="badge bg-secondary px-3 py-2">{{ index + 1 }}</span>
      </div>
      <div class="col-12 col-md-4">
        <input
          type="text"
          :aria-label="question.topic"
          class="form-control shadow-sm"
          v-model="question.topic"
          placeholder="Enter Topic"
          :disabled="editableQuestionId !== question.uuid"
        />
      </div>
      <div class="col-12 col-md-4">
        <input
          type="text"
          :aria-label="question.questionText"
          class="form-control shadow-sm"
          v-model="question.questionText"
          placeholder="Enter Question Text"
          :disabled="editableQuestionId !== question.uuid"
        />
      </div>
      <div
        class="col-12 col-md-1 text-end"
        v-if="editableQuestionId !== question.uuid"
      >
        <button
          class="btn btn-warning fs-4 py-0 px-2"
          aria-label="Edit"
          @click="enableEditing(question.uuid)"
          data-bs-toggle="tooltip"
          data-bs-placement="right"
          title="Edit"
        >
          <i class="bi bi-pencil"></i>
        </button>
      </div>
      <div
        class="col-12 col-md-1 text-end"
        v-if="editableQuestionId === question.uuid"
      >
        <button
          class="btn btn-success fs-4 py-0 px-2"
          aria-label="Save"
          @click="saveQuestion(question)"
          data-bs-toggle="tooltip"
          data-bs-placement="right"
          title="Save Changes"
        >
          <i class="bi bi-box-arrow-down"></i>
        </button>
      </div>
      <div class="col-12 col-md-1 text-end">
        <button
          class="btn btn-primary fs-4 py-0 px-2"
          aria-label="Edit Recommendations"
          @click="editRecommendations(question.uuid)"
          data-bs-toggle="tooltip"
          data-bs-placement="right"
          title="Edit Recommendations"
        >
          <i class="bi bi-pencil-square"></i>
        </button>
      </div>
      <div class="col-12 col-md-1 text-end">
        <button
          class="btn btn-danger fs-4 py-0 px-2"
          aria-label="Delete"
          data-bs-toggle="tooltip"
          data-bs-placement="right"
          title="Delete"
          @click="removeQuestion(question)"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-12 col-md-1"></div>
      <div class="col-12 col-md-4">
        <input
          type="text"
          class="form-control shadow-sm"
          v-model="newTopic"
          placeholder="Enter Topic"
        />
      </div>
      <div class="col-12 col-md-5">
        <input
          type="text"
          class="form-control shadow-sm"
          v-model="newQuestionText"
          placeholder="Enter Question Text"
        />
      </div>
      <div class="col-12 col-md-1 text-end">
        <button class="btn btn-success fs-4 py-0 px-2" @click="pushQuestion">
          <i class="bi bi-plus"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";
import ContentAPI from "@/helpers/ContentAPI";

export default {
  name: "AdminControlQuestions",
  mixins: [ContentAPI],
  data() {
    return {
      newTopic: "",
      newQuestionText: "",
      editIndex: null,
      editableQuestionId: null,
      isLoading: false,
      page: "Sheet1"
    };
  },
  computed: {
    ...mapGetters(["getQuestionData"]),
    questions() {
      return this.getQuestionData;
    }
  },
  methods: {
    ...mapActions(["fetchQuestionData", "fetchRecommendationData"]),
    pushQuestion() {
      this.isLoading = true;
      if (this.newTopic.trim() === "" || this.newQuestionText.trim() === "") {
        alert("Please enter both topic and question text before submitting.");
        this.isLoading = false;
        return;
      }
      let payload = {
        topic: this.newTopic,
        question_text: this.newQuestionText,
        question_id: uuidv4(),
        question_uuid: uuidv4()
      };
      let page = this.page;
      this.createContentEntry(payload, page, () => {
        this.isLoading = false;
        this.fetchQuestionData();
      });
      this.newTopic = "";
      this.newQuestionText = "";
    },
    removeQuestion(question) {
      this.isLoading = true;
      let payload = {
        uid_key: "question_uuid",
        uid_value: question.uuid
      };
      let page = this.page;
      this.deleteContentEntry(payload, page, () => {
        this.isLoading = false;
        this.fetchQuestionData();
      });
    },
    enableEditing(uuid) {
      this.editableQuestionId = uuid;
    },
    saveQuestion(question) {
      this.isLoading = true;
      let payload = {
        topic: question.topic,
        question_text: question.questionText,
        question_uuid: question.uuid
      };
      let page = this.page;
      this.updateContentEntry(payload, page, () => {
        this.isLoading = false;
        this.fetchQuestionData();
      });
      this.editableQuestionId = null;
    },
    editRecommendations(uuid) {
      this.$emit("edit-recommendations", uuid);
    }
  }
};
</script>

<style scoped>
/*  custom styles to put the loading bar over the component and not allow interactivity while endpoints are being called */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>
